import {
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  AfterViewInit,
  OnInit,
  Input,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { gsap, Power2, Elastic } from 'gsap';
import { CustomerService } from '../../services/customer.service';
import { ToastrService } from 'ngx-toastr';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../states/app.state';
import { RoleService } from '../../services/roles/role.service';
import { User } from '../../models/user.model';
import { selectUser } from '../../states/user-details';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-comic-feel-chat',
  templateUrl: './comic-feel-chat.component.html',
  styleUrls: ['./comic-feel-chat.component.scss'],
})
export class ComicFeelChatComponent
  implements AfterViewInit, OnInit, OnChanges
{
  @ViewChildren('bubble') bubbles!: QueryList<ElementRef>;
  @Input() filter: string = 'homepage';
  @Input() filterId: number = null;
  @Input() topPosition: string = '10rem';
  messages: any[] = [];

  // Modal properties
  showModal = false;
  newMessage = {
    text: '',
    from: 'left',
    avatar: '1',
  };

  // Available avatars
  availableAvatars = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
  user: User;
  isAdmin: boolean = false;

  constructor(
    private customerService: CustomerService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>,
    private roleService: RoleService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.store.pipe(select(selectUser)).subscribe((user: User) => {
      this.user = user;
      this.roleService.rolesSubject.subscribe((response) => {
        this.isAdmin = response?.userType?.includes('ADMIN');
        this.cdr.detectChanges();
      });
      this.cdr.detectChanges();
    });
    this.getComicChats();
  }

  ngAfterViewInit() {
    setTimeout(() => this.animateBubbles(), 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['filterId']) {
      this.getComicChats();
    }
  }

  getComicChats() {
    this.customerService
      .getComicChats({
        filter: this.filter,
        filterId: this.filterId,
      })
      .subscribe({
        next: (response) => {
          // Ensure we have valid data
          if (response && response.body && response.body.chats) {
            this.messages = [...response.body.chats]; // Create new array reference
          } else {
            this.messages = [];
          }

          // Force change detection
          this.cdr.detectChanges();
          this.cdr.markForCheck();

          // Re-animate bubbles with new data
          setTimeout(() => this.animateBubbles(), 100);
        },
        error: (error) => {
          console.error('Error fetching comic chats:', error);
          this.messages = [];
          this.cdr.detectChanges();
        },
      });
  }

  animateBubbles() {
    const tl = gsap.timeline();

    this.bubbles.forEach((elRef, index) => {
      const row = elRef.nativeElement;
      const bubble = row.querySelector('.bubble');
      const avatar = row.querySelector('.avatar');
      // const textEl = row.querySelector('.bubble-text');

      // const fullText = textEl.textContent;
      // textEl.textContent = ''; // clear text initially

      gsap.set(row, { opacity: 0, y: 20, scale: 0.9 });
      gsap.set(bubble, { scale: 0.8 });

      // Bubble + avatar animation
      tl.to(row, {
        opacity: 1,
        y: 0,
        scale: 1,
        ease: Power2.easeOut,
        duration: 0.3,
      })
        .to(bubble, {
          scale: 1.1,
          duration: 0.15,
          ease: Elastic.easeOut.config(1, 0.6),
        })
        .to(bubble, {
          scale: 1,
          duration: 0.15,
          ease: Elastic.easeOut.config(1, 0.6),
        })
        .fromTo(
          avatar,
          { scale: 0 },
          { scale: 1, ease: Elastic.easeOut.config(1, 0.5), duration: 0.4 },
          '-=0.4'
        );

      // // Typing effect
      // tl.add(() => {
      //   textEl.classList.add('typing');
      //   this.typeText(textEl, fullText, () => {
      //     textEl.classList.remove('typing');
      //   });
      // });

      // // Wait for typing to finish before next bubble
      // tl.to({}, { duration: fullText.length * 0.05 + 0.3 });
    });
  }

  typeText(element: HTMLElement, text: string, onComplete?: () => void) {
    let i = 0;
    const speed = 40; // typing speed in ms

    const interval = setInterval(() => {
      element.textContent += text.charAt(i);
      i++;
      if (i >= text.length) {
        clearInterval(interval);
        if (onComplete) onComplete();
      }
    }, speed);
  }

  // Modal methods
  private modalRef: NgbModalRef;

  openAddMessageModal(template) {
    // this.showModal = true;
    this.modalRef = this.modalService.open(template, {
      windowClass: 'my-modal',
      size: 'md',
    });

    this.resetNewMessage();
  }
  closeModal() {
    // this.showModal = false;
     this.modalRef.close();
    this.resetNewMessage();
  }

  resetNewMessage() {
    this.newMessage = {
      text: '',
      from: 'left',
      avatar: '1',
    };
  }

  selectAvatar(avatar: string) {
    this.newMessage.avatar = avatar;
  }

  addNewMessage() {
    if (this.newMessage.text.trim()) {
      const payload = {
        text: this.newMessage.text.trim(),
        from: this.newMessage.from,
        avatar: this.newMessage.avatar,
        filter: this.filter,
        filterId: this.filterId,
      };

      console.log('Adding new message with payload:', payload);

      this.customerService.addComicChat(payload).subscribe({
        next: (response) => {
          console.log('Add message response:', response);
          this.closeModal();
          this.toastr.success('Chat added successfully');

          // Clear current messages and refresh
          this.messages = [];
          this.cdr.detectChanges();

          // Fetch fresh data
          setTimeout(() => {
            this.getComicChats();
          }, 100);
        },
        error: (error) => {
          this.toastr.error('Error adding comic chat');
          console.error('Error adding comic chat:', error);
        },
      });
    }
  }

  deleteMessage(id: number) {
    if (confirm('Are you sure you want to delete this message?')) {
      console.log('Deleting message with id:', id);

      this.customerService.deleteComicChat(id).subscribe({
        next: (response) => {
          console.log('Delete message response:', response);
          this.toastr.success('Message deleted successfully');

          // Clear current messages and refresh
          this.messages = [];
          this.cdr.detectChanges();

          // Fetch fresh data
          setTimeout(() => {
            this.getComicChats();
          }, 100);
        },
        error: (error) => {
          this.toastr.error('Error deleting message');
          console.error('Error deleting comic chat:', error);
        },
      });
    }
  }
}
